<template>
    <v-card elevation="3" rounded="lg" class="mb-4 getting-started-card">
        <v-card-text class="pa-4">
            <!-- Header -->
            <div class="d-flex align-center mb-3">
                <!-- Progress Ring -->
                <div class="progress-ring mr-3">
                    <svg viewBox="0 0 44 44" width="44" height="44">
                        <circle cx="22" cy="22" r="18" fill="none" stroke="#E0E0E0" stroke-width="4"/>
                        <circle cx="22" cy="22" r="18" fill="none" stroke="#551A8B" stroke-width="4"
                                stroke-linecap="round"
                                :stroke-dasharray="circumference"
                                :stroke-dashoffset="circumference - (circumference * onboarding.progressPercent / 100)"
                                transform="rotate(-90 22 22)"
                                class="progress-ring__circle"/>
                    </svg>
                    <span class="progress-ring__text">{{ onboarding.progressPercent }}%</span>
                </div>
                <div class="flex-grow-1">
                    <div class="text-subtitle-1 font-weight-bold">Getting Started</div>
                    <div class="text-caption text-grey">{{ onboarding.completedMilestones }} of {{ onboarding.totalMilestones }} complete</div>
                </div>
                <v-btn icon size="x-small" variant="text" @click="confirmDismiss = true">
                    <v-icon size="18" color="grey-lighten-1">mdi-close</v-icon>
                </v-btn>
            </div>

            <!-- Checklist Items -->
            <div class="checklist-items">
                <!-- Create Account (always checked) -->
                <div class="checklist-item checklist-item--completed">
                    <v-icon size="24" color="primary" class="mr-3">mdi-check-circle</v-icon>
                    <span class="checklist-item__label text-decoration-line-through text-grey">Create your account</span>
                </div>

                <!-- Activate Device -->
                <div class="checklist-item" :class="{'checklist-item--completed': onboarding.hasActivatedDevice, 'cursor-pointer': !onboarding.hasActivatedDevice}"
                     @click="!onboarding.hasActivatedDevice && $emit('activateDevice')">
                    <v-icon size="24" :color="onboarding.hasActivatedDevice ? 'primary' : 'grey-lighten-2'" class="mr-3">
                        {{ onboarding.hasActivatedDevice ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                    </v-icon>
                    <span class="checklist-item__label flex-grow-1"
                          :class="onboarding.hasActivatedDevice ? 'text-decoration-line-through text-grey' : 'font-weight-medium'">
                        Activate your first device
                    </span>
                    <v-icon v-if="!onboarding.hasActivatedDevice" size="18" color="grey">mdi-chevron-right</v-icon>
                </div>

                <!-- Add Asset -->
                <div class="checklist-item" :class="{'checklist-item--completed': onboarding.hasCreatedAsset, 'cursor-pointer': !onboarding.hasCreatedAsset}"
                     @click="!onboarding.hasCreatedAsset && $emit('addAsset')">
                    <v-icon size="24" :color="onboarding.hasCreatedAsset ? 'primary' : 'grey-lighten-2'" class="mr-3">
                        {{ onboarding.hasCreatedAsset ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                    </v-icon>
                    <span class="checklist-item__label flex-grow-1"
                          :class="onboarding.hasCreatedAsset ? 'text-decoration-line-through text-grey' : 'font-weight-medium'">
                        Add your first asset
                    </span>
                    <v-icon v-if="!onboarding.hasCreatedAsset" size="18" color="grey">mdi-chevron-right</v-icon>
                </div>

                <!-- Add Driver -->
                <div class="checklist-item" :class="{'checklist-item--completed': onboarding.hasAddedDriver, 'cursor-pointer': !onboarding.hasAddedDriver}"
                     @click="!onboarding.hasAddedDriver && $emit('addDriver')">
                    <v-icon size="24" :color="onboarding.hasAddedDriver ? 'primary' : 'grey-lighten-2'" class="mr-3">
                        {{ onboarding.hasAddedDriver ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                    </v-icon>
                    <span class="checklist-item__label flex-grow-1"
                          :class="onboarding.hasAddedDriver ? 'text-decoration-line-through text-grey' : 'font-weight-medium'">
                        Add your first driver
                    </span>
                    <v-icon v-if="!onboarding.hasAddedDriver" size="18" color="grey">mdi-chevron-right</v-icon>
                </div>

                <!-- Add Location -->
                <div class="checklist-item" :class="{'checklist-item--completed': onboarding.hasAddedLocation, 'cursor-pointer': !onboarding.hasAddedLocation}"
                     @click="!onboarding.hasAddedLocation && $emit('addLocation')">
                    <v-icon size="24" :color="onboarding.hasAddedLocation ? 'primary' : 'grey-lighten-2'" class="mr-3">
                        {{ onboarding.hasAddedLocation ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                    </v-icon>
                    <span class="checklist-item__label flex-grow-1"
                          :class="onboarding.hasAddedLocation ? 'text-decoration-line-through text-grey' : 'font-weight-medium'">
                        Set up your main location
                    </span>
                    <v-icon v-if="!onboarding.hasAddedLocation" size="18" color="grey">mdi-chevron-right</v-icon>
                </div>

                <!-- View Live Map -->
                <div class="checklist-item" :class="{'checklist-item--completed': onboarding.hasViewedLiveMap, 'cursor-pointer': !onboarding.hasViewedLiveMap}"
                     @click="!onboarding.hasViewedLiveMap && $emit('viewLiveMap')">
                    <v-icon size="24" :color="onboarding.hasViewedLiveMap ? 'primary' : 'grey-lighten-2'" class="mr-3">
                        {{ onboarding.hasViewedLiveMap ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                    </v-icon>
                    <span class="checklist-item__label flex-grow-1"
                          :class="onboarding.hasViewedLiveMap ? 'text-decoration-line-through text-grey' : 'font-weight-medium'">
                        View your fleet on the map
                    </span>
                    <v-icon v-if="!onboarding.hasViewedLiveMap" size="18" color="grey">mdi-chevron-right</v-icon>
                </div>

                <!-- Check Trips -->
                <div class="checklist-item" :class="{'checklist-item--completed': onboarding.hasViewedTrips, 'cursor-pointer': !onboarding.hasViewedTrips}"
                     @click="!onboarding.hasViewedTrips && $emit('viewTrips')">
                    <v-icon size="24" :color="onboarding.hasViewedTrips ? 'primary' : 'grey-lighten-2'" class="mr-3">
                        {{ onboarding.hasViewedTrips ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                    </v-icon>
                    <span class="checklist-item__label flex-grow-1"
                          :class="onboarding.hasViewedTrips ? 'text-decoration-line-through text-grey' : 'font-weight-medium'">
                        Check your trip history
                    </span>
                    <v-icon v-if="!onboarding.hasViewedTrips" size="18" color="grey">mdi-chevron-right</v-icon>
                </div>
            </div>
        </v-card-text>

        <!-- Dismiss Confirmation Dialog -->
        <v-dialog v-model="confirmDismiss" max-width="360">
            <v-card>
                <v-card-title class="text-h6">Hide Getting Started?</v-card-title>
                <v-card-text>You can always access these features from the sidebar menu.</v-card-text>
                <v-card-actions>
                    <v-spacer/>
                    <v-btn variant="text" @click="confirmDismiss = false">Cancel</v-btn>
                    <v-btn color="primary" variant="text" @click="dismiss">Hide</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-card>
</template>

<script setup>
import {ref} from 'vue';
import {useOnboardingStore} from '@/stores/onboardingStore';

const onboarding = useOnboardingStore();
const confirmDismiss = ref(false);
const circumference = 2 * Math.PI * 18; // r=18

defineEmits(['activateDevice', 'addAsset', 'addDriver', 'addLocation', 'viewLiveMap', 'viewTrips']);

const dismiss = () => {
    onboarding.dismissChecklist();
    confirmDismiss.value = false;
};
</script>

<style scoped>
.getting-started-card {
    border-left: 4px solid #551A8B;
}

.progress-ring {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
}

.progress-ring__circle {
    transition: stroke-dashoffset 0.5s ease;
}

.progress-ring__text {
    position: absolute;
    font-size: 11px;
    font-weight: 700;
    color: #551A8B;
}

.checklist-items {
    display: flex;
    flex-direction: column;
}

.checklist-item {
    display: flex;
    align-items: center;
    padding: 6px 8px;
    border-radius: 8px;
    transition: background-color 0.15s;
}

.checklist-item:not(.checklist-item--completed):hover {
    background-color: #F5F5F5;
}

.checklist-item__label {
    font-size: 14px;
}
</style>
